import * as React from 'react'
import { ActivityIndicator, Image, Platform, Share, View } from 'react-native'
import { Button, Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import * as FileSystem from 'expo-file-system'
import * as Sentry from '@sentry/react-native'
import Toast from 'react-native-root-toast'
import { MelddModal } from '../../../@ui/containers/modals'
import Column from '../../../@ui/containers/grid/column'
import { MessageType } from '../../../utils/message-type'

function dataUrlToBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',')
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
}

function safeFileSegment(code: string): string {
  return code.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 48) || 'coupon'
}

function documentDirUri(): string {
  const fs = FileSystem as typeof FileSystem & {
    Paths?: { document: { uri: string } }
    documentDirectory?: string | null
  }
  const fromPaths = fs.Paths?.document?.uri
  if (fromPaths) {
    return fromPaths.endsWith('/') ? fromPaths : `${fromPaths}/`
  }
  const doc = fs.documentDirectory
  if (!doc) {
    throw new Error('Document directory is not available')
  }
  return doc.endsWith('/') ? doc : `${doc}/`
}

/**
 * One PNG: wrapped intro text + QR (web only). Encodes the invite link in the QR.
 */
async function buildCompositeInviteCardPngDataUrl(
  intro: string,
  qrDataUrl: string,
  cardWidth: number
): Promise<string> {
  if (typeof document === 'undefined') {
    throw new Error('Composite invite card requires a browser')
  }
  const padding = 16
  const qrSize = Math.min(280, cardWidth - 2 * padding)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas 2D context not available')
  }

  ctx.font = '16px system-ui, -apple-system, Segoe UI, sans-serif'
  const maxTextWidth = cardWidth - 2 * padding

  const wrapParagraphToLines = (paragraph: string): string[] => {
    const out: string[] = []
    let line = ''
    for (let i = 0; i < paragraph.length; i++) {
      const ch = paragraph[i]!
      const next = line + ch
      if (ctx.measureText(next).width > maxTextWidth && line) {
        out.push(line)
        line = ch
      } else {
        line = next
      }
    }
    if (line) out.push(line)
    return out
  }

  const lines: string[] = []
  for (const paragraph of intro.split(/\n+/)) {
    const trimmed = paragraph.trim()
    if (!trimmed) continue
    lines.push(...wrapParagraphToLines(trimmed))
  }
  const lineHeight = 22
  const textBlockHeight = Math.max(lines.length * lineHeight, lineHeight)

  canvas.width = cardWidth
  canvas.height = padding + textBlockHeight + padding + qrSize + padding
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#1a1a1a'
  ctx.textBaseline = 'top'
  lines.forEach((ln, i) => {
    ctx.fillText(ln, padding, padding + i * lineHeight)
  })

  const img = document.createElement('img')
  img.crossOrigin = 'anonymous'
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('Failed to load QR image'))
    img.src = qrDataUrl
  })
  const qrX = (cardWidth - qrSize) / 2
  ctx.drawImage(img, qrX, padding + textBlockHeight + padding, qrSize, qrSize)

  return canvas.toDataURL('image/png')
}

export type CouponInviteQrModalProps = {
  visible: boolean
  onDismiss: () => void
  couponCode: string
  inviteUrl: string
}

export function CouponInviteQrModal({
  visible,
  onDismiss,
  couponCode,
  inviteUrl,
}: CouponInviteQrModalProps) {
  const { t } = useTranslation('admin')
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  const invitationIntro = t('coupon-invite-intro')

  React.useEffect(() => {
    if (!visible || !inviteUrl) {
      setQrDataUrl(null)
      return
    }
    let cancelled = false
    QRCode.toDataURL(inviteUrl, { width: 280, margin: 2 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch((err) => {
        console.error('Coupon invite QR failed', err)
        Sentry.captureException(err)
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [visible, inviteUrl])

  const baseName = safeFileSegment(couponCode)

  const downloadQrCode = async () => {
    if (!qrDataUrl) {
      Toast.show(t('coupon-invite-qr-missing'), MessageType.error)
      return
    }
    setBusy(true)
    try {
      if (Platform.OS === 'web') {
        const composite = await buildCompositeInviteCardPngDataUrl(
          invitationIntro,
          qrDataUrl,
          320
        )
        const res = await fetch(composite)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `meldd-coupon-${baseName}-invite.png`
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else {
        const base64 = dataUrlToBase64(qrDataUrl)
        const fileUri = `${documentDirUri()}meldd-coupon-${baseName}-qr.png`
        await FileSystem.writeAsStringAsync(fileUri, base64, {
          encoding: 'base64',
        })
        await Share.share({
          message: invitationIntro,
          url: fileUri,
          title: t('coupon-invite-download-qr'),
        })
      }
      Toast.show(t('coupon-invite-download-ok'), MessageType.info)
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      Toast.show(t('coupon-invite-download-failed'), MessageType.error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <MelddModal
      visible={visible}
      onDismiss={onDismiss}
      title={t('coupon-invite-qr-title', { code: couponCode })}
      scrollable
      body={
        <Column gap={3} style={{ width: '100%' }}>
          <Text variant="bodyMedium" style={{ textAlign: 'center' }}>
            {invitationIntro}
          </Text>
          <View style={{ alignItems: 'center', paddingVertical: 8 }}>
            {qrDataUrl ? (
              <Image
                source={{ uri: qrDataUrl }}
                style={{ width: 280, height: 280 }}
                accessibilityLabel={t('coupon-invite-qr-image-a11y')}
              />
            ) : (
              <ActivityIndicator size="large" />
            )}
          </View>
          <Button
            mode="outlined"
            onPress={() => void downloadQrCode()}
            disabled={!qrDataUrl || busy}
            loading={busy}
          >
            {t('coupon-invite-download-qr')}
          </Button>
        </Column>
      }
      actions={<Button onPress={onDismiss}>{t('cancel')}</Button>}
    />
  )
}
